export type PlanType = "Free" | "Trial" | "Monthly" | "Yearly";

export type AccessConfig = {
  canBrowseAds: boolean;
  maxFolders: number;
  maxBoards: number; // per folder
  maxAdsPerFolder: number;
  canShareFolders: boolean;
  canInviteCollaborators: boolean;
  premiumFeatures: boolean;
  hasTrial: boolean;
  planType: PlanType;
};

const MAX_UNLIMITED = 9999;

const premiumAccess: Omit<AccessConfig, "planType"> = {
  canBrowseAds: true,
  maxFolders: MAX_UNLIMITED,
  maxBoards: MAX_UNLIMITED,
  maxAdsPerFolder: MAX_UNLIMITED,
  canShareFolders: true,
  canInviteCollaborators: true,
  premiumFeatures: true,
  hasTrial: false,
};

const planAccessMap: Record<PlanType, AccessConfig> = {
  Free: {
    canBrowseAds: true,
    maxFolders: 1,
    maxBoards: 1,
    maxAdsPerFolder: 5,
    canShareFolders: false,
    canInviteCollaborators: false,
    premiumFeatures: false,
    hasTrial: false,
    planType: "Free",
  },
  Trial: { ...premiumAccess, hasTrial: true, planType: "Trial" },
  Monthly: { ...premiumAccess, planType: "Monthly" },
  Yearly: { ...premiumAccess, planType: "Yearly" },
};

/**
 * Returns the access configuration for a given plan type
 */
export const getPlanAccess = (planType?: string): AccessConfig =>
  planAccessMap[planType as PlanType] ?? planAccessMap.Free;

/**
 * Chooses correct access config depending on whether
 * user is parent (activePlan) or invited child (userDetails).
 */
export const getAccessConfig = (
  activePlan: any,
  userDetails: any
): AccessConfig => {
  const planType =
    activePlan?.activePlanDetails?.planType ||
    userDetails?.parentPlanDetails?.planType;

  return getPlanAccess(planType);
};

/**
 * Validates folder count against plan limits
 */
export const isFolderOverLimit = (
  folderIndex: number,
  accessConfig: AccessConfig
): boolean => {
  return folderIndex >= (accessConfig?.maxFolders ?? 0);
};

/**
 * Validates board count per folder against plan limits
 */
export const isBoardOverLimit = (
  boardIndex: number,
  folderIndex: number,
  accessConfig: AccessConfig
): boolean => {
  return (
    folderIndex >= (accessConfig?.maxFolders ?? 0) ||
    boardIndex >= (accessConfig?.maxBoards ?? 0)
  );
};
